import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Fade,
  Alert,
  CircularProgress,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  TableSortLabel,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { es } from 'date-fns/locale';
import { format as formatDateFns } from 'date-fns';
import { api } from '../services/api';

// Utils to derive distinctive, readable vendor colors
const stringToColor = (str?: string): string => {
  if (!str) return '#64748b'; // slate-500 fallback
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  // Map hash to pleasant HSL (avoids too light/dark)
  const h = hash % 360;
  const s = 65; // saturation
  const l = 45; // lightness
  return `hsl(${h}, ${s}%, ${l}%)`;
};

const hslToRgba = (hsl: string, alpha: number): string => {
  // hsl(h, s%, l%) → rgba
  const match = hsl.match(/hsl\((\d+),\s*(\d+)%?,\s*(\d+)%?\)/i);
  if (!match) return 'rgba(100,100,100,' + alpha + ')';
  let h = parseInt(match[1], 10) / 360;
  let s = parseInt(match[2], 10) / 100;
  let l = parseInt(match[3], 10) / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${alpha})`;
};

interface Sale {
  id: number;
  customer_id?: number;
  customer_name: string;
  customer_phone: string;
  product_id?: number;
  product_name: string;
  product_description: string;
  vendor_name?: string;
  collector_name?: string;
  notes?: string;
  total_amount: number;
  cost_price: number;
  installments: number;
  installment_amount: number;
  total_paid: number;
  created_at: string;
  start_date?: string;
  status: 'active' | 'completed';
  payments_count: number;
  arrears_days?: number;
  has_arrears?: boolean;
  has_partial_installments?: boolean;
  partial_outstanding_amount?: number;
  collection_day?: number;
}

const COLLECTION_DAY_LABELS = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

interface InstallmentDetail {
  installment_number: number;
  total_installments: number;
  original_due_date: string;
  due_date: string;
  is_rescheduled: boolean;
  original_amount: number;
  current_amount: number;
  is_paid: boolean;
  payment_date?: string | null;
  previous_due_date?: string | null;
  reschedule_count?: number;
  payments_count?: number;
  total_paid_amount?: number;
  remaining_balance?: number;
  is_partial?: boolean;
  last_payment_date?: string | null;
  last_payment_note?: string | null;
  last_reschedule_reason?: string | null;
}

interface CollectionRecord {
  sale_id: number;
  notes?: string;
  customer_name: string;
  customer_surname: string;
  customer_address: string;
  customer_phone?: string | null;
  product_name: string;
  installment_number: number;
  total_installments: number;
  installment_amount: number;
  is_paid: boolean;
  is_rescheduled: boolean;
  due_date: string;
  cash_amount?: number;
  transfer_amount?: number;
  payment_method?: string;
  payment_id?: number;
  payment_date?: string;
  total_paid_amount?: number;
  remaining_balance?: number;
  payments_count?: number;
  is_partial?: boolean;
  last_payment_date?: string | null;
  last_payment_note?: string | null;
  last_reschedule_reason?: string | null;
}

export default function SalesTracking() {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [moraFilter, setMoraFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [collectorFilter, setCollectorFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'customer' | 'vendor'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc'); // por defecto: más reciente primero
  const [error, setError] = useState<string | null>(null);
  const [installmentsDialog, setInstallmentsDialog] = useState({
    open: false,
    sale: null as Sale | null,
    loading: false,
    error: null as string | null,
    success: null as string | null,
    installments: [] as InstallmentDetail[],
  });
  const [savingInstallment, setSavingInstallment] = useState<number | null>(null);
  const [balancePaymentDialog, setBalancePaymentDialog] = useState({
    open: false,
    saleId: 0,
    installmentNumber: 0,
    customerName: '',
    remaining: 0,
  });
  const [balancePaymentForm, setBalancePaymentForm] = useState({
    amount: 0,
    payment_method: 'cash' as 'cash' | 'transfer' | 'mixed',
    cash_amount: 0,
    transfer_amount: 0,
    notes: '',
  });
  const [balancePaymentLoading, setBalancePaymentLoading] = useState(false);
  const [balancePaymentError, setBalancePaymentError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'tracking' | 'collections'>('tracking');
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [dailyCollections, setDailyCollections] = useState<CollectionRecord[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState<string | null>(null);
  const [collectionsStatusFilter, setCollectionsStatusFilter] = useState<'all' | 'pending' | 'rescheduled' | 'paid'>('all');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | ''>('');
  const [selectedSaleId, setSelectedSaleId] = useState<number | ''>('');
  const [saleOperations, setSaleOperations] = useState<InstallmentDetail[]>([]);
  const [saleOperationsLoading, setSaleOperationsLoading] = useState(false);
  const [saleOperationsError, setSaleOperationsError] = useState<string | null>(null);
  
  const [collectionDayDialog, setCollectionDayDialog] = useState({
    open: false,
    saleId: 0,
    customerName: '',
    currentDay: 0,
    newDay: 0,
  });
  const [savingCollectionDay, setSavingCollectionDay] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const openCollectionDayDialog = (sale: Sale) => {
    setCollectionDayDialog({
      open: true,
      saleId: sale.id,
      customerName: sale.customer_name,
      currentDay: sale.collection_day || 1,
      newDay: sale.collection_day || 1,
    });
  };

  const handleSaveCollectionDay = async () => {
    if (collectionDayDialog.newDay === collectionDayDialog.currentDay) {
      setCollectionDayDialog((prev) => ({ ...prev, open: false }));
      return;
    }
    setSavingCollectionDay(true);
    try {
      await api.put(`/sales/${collectionDayDialog.saleId}`, {
        collection_day: collectionDayDialog.newDay,
      });
      setSales((prev) =>
        prev.map((s) =>
          s.id === collectionDayDialog.saleId
            ? { ...s, collection_day: collectionDayDialog.newDay }
            : s
        )
      );
      setCollectionDayDialog((prev) => ({ ...prev, open: false }));
    } catch (err) {
      console.error('Error updating collection day:', err);
    } finally {
      setSavingCollectionDay(false);
    }
  };

  const fetchSales = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/sales/tracking');
      const data = response.data;
      const normalizedSales = (data.sales || []).map((sale: any) => ({
        ...sale,
        total_paid: Number(sale.total_paid || 0),
        has_arrears: Boolean(sale.has_arrears),
        arrears_days: Number(sale.arrears_days || 0),
        has_partial_installments: Boolean(sale.has_partial_installments),
        partial_outstanding_amount: Number(sale.partial_outstanding_amount || 0),
      }));
      setSales(normalizedSales);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setError('Error al cargar las ventas');
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = useCallback(async (targetDate: Date) => {
    setCollectionsLoading(true);
    setCollectionsError(null);
    try {
      const formattedDate = formatDateFns(targetDate, 'yyyy-MM-dd');
      const response = await api.get('/collections/daily', {
        params: { date: formattedDate },
      });
      const data: CollectionRecord[] = response.data || [];
      setDailyCollections(data);
    } catch (fetchError: any) {
      console.error('Error fetching collections:', fetchError);
      const message = fetchError?.response?.data?.detail || 'Error al cargar el registro de cobranzas';
      setCollectionsError(message);
      setDailyCollections([]);
    } finally {
      setCollectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSales();
  }, []);

  useEffect(() => {
    if (activeTab === 'collections' && selectedDate) {
      fetchCollections(selectedDate);
    }
  }, [activeTab, selectedDate, fetchCollections]);

  useEffect(() => {
    if (activeTab === 'tracking') {
      setPage(0);
    } else if (activeTab === 'collections') {
      setSelectedCustomerId('');
      setSelectedSaleId('');
      setSaleOperations([]);
      setSaleOperationsError(null);
    }
  }, [activeTab]);

  const filteredSales = sales.filter((sale) => {
    const matchesSearch =
      sale.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.customer_phone.includes(searchTerm);

    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;

    const arrearsDays = sale.arrears_days ?? 0;
    const hasArrears = (sale.has_arrears ?? false) || (sale.has_partial_installments ?? false) || arrearsDays > 0;
    const matchesMora =
      moraFilter === 'all' || (moraFilter === 'yes' ? hasArrears : !hasArrears);

    const matchesVendor = vendorFilter === 'all' || (vendorFilter === 'none' ? !sale.vendor_name : sale.vendor_name === vendorFilter);
    const matchesCollector = collectorFilter === 'all' || (collectorFilter === 'none' ? !sale.collector_name : sale.collector_name === collectorFilter);
    return matchesSearch && matchesStatus && matchesMora && matchesVendor && matchesCollector;
  });

  const sortedSales = useMemo(() => {
    const data = [...filteredSales];
    const compare = (a: any, b: any) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      if (sortBy === 'date') {
        const ad = new Date(a.created_at).getTime();
        const bd = new Date(b.created_at).getTime();
        return (ad - bd) * dir;
      }
      if (sortBy === 'customer') {
        const an = (a.customer_name || '').toLowerCase();
        const bn = (b.customer_name || '').toLowerCase();
        return an.localeCompare(bn) * dir;
      }
      // vendor
      const av = (a.vendor_name || '').toLowerCase();
      const bv = (b.vendor_name || '').toLowerCase();
      return av.localeCompare(bv) * dir;
    };
    data.sort(compare);
    return data;
  }, [filteredSales, sortBy, sortDirection]);

  const vendorOptions = useMemo(() => {
    const set = new Set<string>();
    sales.forEach((s) => {
      if ((s as any).vendor_name && (s as any).vendor_name.trim()) set.add((s as any).vendor_name.trim());
    });
    return Array.from(set).sort();
  }, [sales]);

  const collectorOptions = useMemo(() => {
    const set = new Set<string>();
    sales.forEach((s) => {
      if ((s as any).collector_name && (s as any).collector_name.trim()) set.add((s as any).collector_name.trim());
    });
    return Array.from(set).sort();
  }, [sales]);

  const vendorColor = (name?: string) => {
    if (!name) return 'default' as const;
    const colors = ['primary','secondary','success','warning','info','error'] as const;
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return colors[hash % colors.length];
  };

  // Export helpers (CSV with .xlsx extension for compatibility)
  const downloadAsXlsx = (filename: string, headers: string[], rows: (string | number)[][]) => {
    const csv = [headers, ...rows]
      .map(r => r.map(v => {
        const s = String(v ?? '');
        // Escape quotes and wrap if needed
        if (s.includes(',') || s.includes('\n') || s.includes('"')) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      }).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportSalesXlsx = () => {
    const headers = ['Fecha', 'Cliente', 'Vendedor', 'Cobrador', 'Producto', 'Costo', 'Valor', 'Ganancia', 'Cuota', 'Cuotas', 'Cobrado', 'Por cobrar', 'Mora (días)'];
    const rows = sortedSales.map((s) => [
      formatDate(s.created_at),
      s.customer_name,
      (s as any).vendor_name || '',
      (s as any).collector_name || '',
      (s as any).notes && (s as any).notes.startsWith('Productos: ') ? (s as any).notes.replace('Productos: ', '') : s.product_name,
      s.cost_price,
      s.total_amount,
      s.total_amount - s.cost_price,
      s.installment_amount,
      `${s.payments_count}/${s.installments}`,
      getCollectedAmount(s),
      getPendingAmount(s),
      s.arrears_days || 0,
    ]);
    downloadAsXlsx(`seguimiento_ventas_${formatDateFns(new Date(), 'yyyy-MM-dd')}`, headers, rows);
  };

  const handleExportCollectionsXlsx = () => {
    const headers = ['Cliente', 'Producto', 'Cuota', 'Monto', 'Estado', 'Nota / Motivo', 'Fecha'];
    const rows = filteredDailyCollections.map((c) => {
      const noteDetail = c.last_payment_note || c.last_reschedule_reason || '';
      return [
        `${c.customer_surname} ${c.customer_name}`.trim(),
        c.notes && c.notes.startsWith('Productos: ') ? c.notes.replace('Productos: ', '') : c.product_name,
        `${c.installment_number}/${c.total_installments}`,
        c.installment_amount,
        c.is_paid ? 'Pagado' : c.is_rescheduled ? 'Reprogramado' : 'Pendiente',
        noteDetail,
        formatDate(c.due_date),
      ];
    });
    const label = selectedDate ? formatDateFns(selectedDate, 'yyyy-MM-dd') : formatDateFns(new Date(), 'yyyy-MM-dd');
    downloadAsXlsx(`cobranzas_${label}`, headers, rows);
  };

  const handleExportOperationsXlsx = () => {
    if (selectedSaleId === '' || saleOperations.length === 0) return;
    const headers = ['Fecha', 'Cuota', 'Estado'];
    const rows = saleOperations.map((op) => {
      const st = getInstallmentStatus(op);
      return [formatDate(op.due_date), `${op.installment_number}/${op.total_installments}`, st.label];
    });
    downloadAsXlsx(`operaciones_venta_${selectedSaleId}_${formatDateFns(new Date(), 'yyyy-MM-dd')}`, headers, rows);
  };

  function roundCurrency(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  function computeInstallmentRemaining(detail: InstallmentDetail): number {
    const backendRemaining = typeof detail.remaining_balance === 'number'
      ? detail.remaining_balance
      : detail.current_amount - (detail.total_paid_amount ?? 0);
    return roundCurrency(Math.max(backendRemaining, 0));
  }

  function isInstallmentPartial(detail: InstallmentDetail): boolean {
    if (detail.is_partial === true) {
      return true;
    }
    const totalPaidAmount = Number(detail.total_paid_amount ?? 0);
    const remaining = computeInstallmentRemaining(detail);
    return totalPaidAmount > 0 && remaining > 0 && !detail.is_paid;
  }

  function normalizeInstallmentDetail(detail: any): InstallmentDetail {
    const originalAmount = Number(detail.original_amount ?? detail.installment_amount ?? 0);
    const currentAmount = Number(detail.current_amount ?? detail.installment_amount ?? 0);
    const totalPaidAmount = Number(detail.total_paid_amount ?? 0);
    const remainingBalanceRaw =
      detail.remaining_balance !== undefined && detail.remaining_balance !== null
        ? Number(detail.remaining_balance)
        : currentAmount - totalPaidAmount;

    const normalized: InstallmentDetail = {
      ...detail,
      original_amount: originalAmount,
      current_amount: currentAmount,
      reschedule_count: Number(detail.reschedule_count || 0),
      payments_count: Number(detail.payments_count || 0),
      total_paid_amount: totalPaidAmount,
      remaining_balance: roundCurrency(Math.max(remainingBalanceRaw, 0)),
      is_paid: Boolean(detail.is_paid),
      is_partial: typeof detail.is_partial === 'boolean' ? detail.is_partial : undefined,
    };

    normalized.is_partial = isInstallmentPartial(normalized);

    return normalized;
  }

  const getCollectedAmount = (sale: Sale) => {
    if (typeof sale.total_paid === 'number' && sale.total_paid > 0) {
      return sale.total_paid;
    }
    return sale.installment_amount * sale.payments_count;
  };

  const getPendingAmount = (sale: Sale) => {
    const collected = getCollectedAmount(sale);
    return Math.max(sale.total_amount - collected, 0);
  };

  const getProgressPercentage = (sale: Sale) => {
    const collected = getCollectedAmount(sale);
    const total = Math.max(collected + getPendingAmount(sale), sale.total_amount);
    if (!total) return 0;
    return (collected / total) * 100;
  };

  const getProfit = (sale: Sale) => {
    return Math.max(sale.total_amount - sale.cost_price, 0);
  };

  const getArrearsHighlight = (days: number) => {
    if (!days) return {};

    const isDark = theme.palette.mode === 'dark';

    if (days >= 28) {
      return isDark
        ? {
            backgroundColor: 'rgba(127, 29, 29, 0.32)',
            borderLeft: `4px solid ${theme.palette.error.light}`,
          }
        : {
            backgroundColor: '#fee2e2',
            borderLeft: '4px solid #b91c1c',
          };
    }

    return isDark
      ? {
          backgroundColor: 'rgba(146, 64, 14, 0.26)',
          borderLeft: `4px solid ${theme.palette.warning.light}`,
        }
      : {
          backgroundColor: '#fef3c7',
          borderLeft: '4px solid #d97706',
        };
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue as 'tracking' | 'collections');
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleCustomerSelect = (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedCustomerId('');
      setSelectedSaleId('');
      setSaleOperations([]);
      setSaleOperationsError(null);
      return;
    }
    const customerId = Number(value);
    setSelectedCustomerId(customerId);
    setSelectedSaleId('');
    setSaleOperations([]);
    setSaleOperationsError(null);
  };

  const fetchSaleOperations = useCallback(async (saleId: number) => {
    setSaleOperationsLoading(true);
    setSaleOperationsError(null);
    try {
      const response = await api.get(`/sales/${saleId}/installments`);
      const installments = (response.data?.installments || []).map(normalizeInstallmentDetail);
      setSaleOperations(installments);
    } catch (fetchError: any) {
      const message = fetchError?.response?.data?.detail || 'No se pudo obtener el historial de la venta.';
      setSaleOperationsError(message);
      setSaleOperations([]);
    } finally {
      setSaleOperationsLoading(false);
    }
  }, []);

  const handleSaleSelect = async (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedSaleId('');
      setSaleOperations([]);
      setSaleOperationsError(null);
      return;
    }
    const saleId = Number(value);
    setSelectedSaleId(saleId);
    await fetchSaleOperations(saleId);
  };

  const getCollectionStatus = (collection: CollectionRecord) => {
    if (collection.is_paid) {
      return { label: 'Cobrado', color: 'success' as const, value: 'paid' as const };
    }
    if (collection.is_partial) {
      return { label: 'Pago parcial', color: 'info' as const, value: 'pending' as const };
    }
    if (collection.is_rescheduled) {
      return { label: 'Reprogramado', color: 'warning' as const, value: 'rescheduled' as const };
    }
    return { label: 'Pendiente', color: 'default' as const, value: 'pending' as const };
  };

  const getInstallmentStatus = (detail: InstallmentDetail) => {
    if (isInstallmentPartial(detail)) {
      return { label: 'Pago parcial', color: 'info' as const };
    }
    if (detail.is_paid) {
      return { label: 'Cobrado', color: 'success' as const };
    }
    if (detail.is_rescheduled) {
      return { label: 'Reprogramado', color: 'warning' as const };
    }
    return { label: 'Pendiente', color: 'default' as const };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    let date: Date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateString);
    }
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleDateString('es-AR');
  };

  const openInstallmentsDialog = async (sale: Sale) => {
    setInstallmentsDialog({ open: true, sale, loading: true, error: null, success: null, installments: [] });

    try {
      const response = await api.get(`/sales/${sale.id}/installments`);
      const installments = (response.data.installments || []).map(normalizeInstallmentDetail);
      setInstallmentsDialog((prev) => ({
        ...prev,
        loading: false,
        installments,
      }));
    } catch (fetchError: any) {
      const message = fetchError?.response?.data?.detail || 'Error al cargar el detalle de cuotas';
      setInstallmentsDialog((prev) => ({
        ...prev,
        loading: false,
        error: message,
      }));
    }
  };

  const closeInstallmentsDialog = () => {
    setSavingInstallment(null);
    closeBalancePaymentDialog();
    setInstallmentsDialog({ open: false, sale: null, loading: false, error: null, success: null, installments: [] });
  };

  const totals = useMemo(() => {
    const summary = filteredSales.reduce(
      (acc, sale) => {
        const cost = sale.cost_price || 0;
        const total = sale.total_amount || 0;
        const profit = getProfit(sale);
        const installmentAmount = sale.installment_amount || 0;
        const collected = getCollectedAmount(sale);
        const pending = getPendingAmount(sale);
        const progress = getProgressPercentage(sale);

        acc.cost += cost;
        acc.total += total;
        acc.profit += profit;
        acc.installmentAmount += installmentAmount;
        acc.collected += collected;
        acc.pending += pending;
        acc.progress += progress;
        acc.count += 1;
        return acc;
      },
      { cost: 0, total: 0, profit: 0, installmentAmount: 0, collected: 0, pending: 0, progress: 0, count: 0 }
    );

    return {
      ...summary,
      averageProgress: summary.count ? summary.progress / summary.count : 0,
    };
  }, [filteredSales]);

  const paginatedSales = sortedSales.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (column: 'date' | 'customer' | 'vendor') => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      // default direction per column
      setSortDirection(column === 'date' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const customersOptions = useMemo(() => {
    const map = new Map<number, string>();
    sales.forEach((sale) => {
      if (sale.customer_id != null && !map.has(sale.customer_id)) {
        map.set(sale.customer_id, sale.customer_name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [sales]);

  const customerSales = useMemo(() => {
    if (selectedCustomerId === '') return [];
    const customerId = Number(selectedCustomerId);
    return sales.filter((sale) => sale.customer_id === customerId);
  }, [sales, selectedCustomerId]);

  const collectionsSummary = useMemo(() => {
    const total = dailyCollections.length;
    let paid = 0;
    let rescheduled = 0;
    let pending = 0;

    dailyCollections.forEach((collection) => {
      if (collection.is_paid) {
        paid += 1;
      } else if (collection.is_rescheduled) {
        rescheduled += 1;
      } else {
        pending += 1;
      }
    });

    return { total, paid, rescheduled, pending };
  }, [dailyCollections]);

  const filteredDailyCollections = useMemo(() => {
    if (collectionsStatusFilter === 'all') {
      return dailyCollections;
    }
    return dailyCollections.filter(
      (collection) => getCollectionStatus(collection).value === collectionsStatusFilter
    );
  }, [dailyCollections, collectionsStatusFilter]);

  const handleCollectionFilterChange = (status: 'all' | 'pending' | 'rescheduled' | 'paid') => {
    setCollectionsStatusFilter((prev) => (prev === status ? 'all' : status));
  };

  const handleCollectionFilterKeyDown = (status: 'all' | 'pending' | 'rescheduled' | 'paid') => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCollectionFilterChange(status);
    }
  };

  const handleInstallmentAmountChange = (installmentNumber: number, value: number) => {
    const numericValue = Number.isFinite(value) ? value : 0;
    setInstallmentsDialog((prev) => ({
      ...prev,
      installments: prev.installments.map((detail) =>
        detail.installment_number === installmentNumber
          ? { ...detail, current_amount: numericValue }
          : detail
      ),
      success: null,
      error: null,
    }));
  };

  const handleSaveInstallmentAmount = async (installmentNumber: number, currentAmount: number) => {
    if (!installmentsDialog.sale) return;
    if (!currentAmount || currentAmount <= 0) {
      setInstallmentsDialog((prev) => ({
        ...prev,
        error: 'El monto de la cuota debe ser mayor a 0.',
        success: null,
      }));
      return;
    }
    setSavingInstallment(installmentNumber);
    setInstallmentsDialog((prev) => ({ ...prev, error: null, success: null }));

    try {
      await api.put(`/sales/${installmentsDialog.sale.id}/installments/${installmentNumber}`, {
        new_amount: currentAmount,
      });

      await fetchSales();

      const refreshed = await api.get(`/sales/${installmentsDialog.sale.id}/installments`);
      const refreshedInstallments = (refreshed.data.installments || []).map(normalizeInstallmentDetail);

      setInstallmentsDialog((prev) => ({
        ...prev,
        installments: refreshedInstallments,
        success: 'Cuota actualizada correctamente.',
      }));
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      let message = 'No se pudo actualizar la cuota.';
      if (Array.isArray(detail)) {
        message = detail.map((item: any) => item?.msg || item?.detail || JSON.stringify(item)).join(' | ');
      } else if (detail && typeof detail === 'object') {
        message = detail.msg || detail.detail || detail.message || JSON.stringify(detail);
      } else if (typeof detail === 'string') {
        message = detail;
      }
      setInstallmentsDialog((prev) => ({ ...prev, error: message }));
    } finally {
      setSavingInstallment(null);
    }
  };

  const openBalancePaymentDialogFor = (detail: InstallmentDetail) => {
    if (!installmentsDialog.sale) return;
    const remaining = computeInstallmentRemaining(detail);
    setBalancePaymentDialog({
      open: true,
      saleId: installmentsDialog.sale.id,
      installmentNumber: detail.installment_number,
      customerName: installmentsDialog.sale.customer_name,
      remaining,
    });
    setBalancePaymentForm({
      amount: remaining,
      payment_method: 'cash',
      cash_amount: remaining,
      transfer_amount: 0,
      notes: '',
    });
    setBalancePaymentError(null);
  };

  const closeBalancePaymentDialog = () => {
    setBalancePaymentDialog({ open: false, saleId: 0, installmentNumber: 0, customerName: '', remaining: 0 });
    setBalancePaymentForm({
      amount: 0,
      payment_method: 'cash',
      cash_amount: 0,
      transfer_amount: 0,
      notes: '',
    });
    setBalancePaymentError(null);
  };

  const handleBalanceAmountChange = (rawValue: number) => {
    const safeValue = Number.isNaN(rawValue) ? 0 : rawValue;
    const clamped = Math.min(Math.max(safeValue, 0), balancePaymentDialog.remaining || 0);
    const normalized = roundCurrency(clamped);
    setBalancePaymentForm((prev) => {
      if (prev.payment_method === 'cash') {
        return {
          ...prev,
          amount: normalized,
          cash_amount: normalized,
          transfer_amount: 0,
        };
      }
      if (prev.payment_method === 'transfer') {
        return {
          ...prev,
          amount: normalized,
          cash_amount: 0,
          transfer_amount: normalized,
        };
      }
      const safeCash = roundCurrency(Math.min(Math.max(prev.cash_amount || 0, 0), normalized));
      return {
        ...prev,
        amount: normalized,
        cash_amount: safeCash,
        transfer_amount: roundCurrency(Math.max(normalized - safeCash, 0)),
      };
    });
  };

  const handleBalanceMethodChange = (method: 'cash' | 'transfer' | 'mixed') => {
    setBalancePaymentForm((prev) => {
      if (method === 'cash') {
        return {
          ...prev,
          payment_method: method,
          cash_amount: prev.amount,
          transfer_amount: 0,
        };
      }
      if (method === 'transfer') {
        return {
          ...prev,
          payment_method: method,
          cash_amount: 0,
          transfer_amount: prev.amount,
        };
      }
      return {
        ...prev,
        payment_method: method,
        cash_amount: prev.cash_amount,
        transfer_amount: prev.transfer_amount,
      };
    });
  };

  const handleBalanceCashChange = (rawValue: number) => {
    setBalancePaymentForm((prev) => {
      const total = prev.amount || 0;
      const safeValue = Math.min(Math.max(Number.isNaN(rawValue) ? 0 : rawValue, 0), total);
      const remaining = Math.max(total - safeValue, 0);
      return {
        ...prev,
        cash_amount: roundCurrency(safeValue),
        transfer_amount: roundCurrency(remaining),
      };
    });
  };

  const handleBalanceTransferChange = (rawValue: number) => {
    setBalancePaymentForm((prev) => {
      const total = prev.amount || 0;
      const safeValue = Math.min(Math.max(Number.isNaN(rawValue) ? 0 : rawValue, 0), total);
      const remaining = Math.max(total - safeValue, 0);
      return {
        ...prev,
        transfer_amount: roundCurrency(safeValue),
        cash_amount: roundCurrency(remaining),
      };
    });
  };

  const handleBalancePaymentSubmit = async () => {
    if (!balancePaymentDialog.open || !balancePaymentDialog.saleId) return;
    setBalancePaymentLoading(true);
    setBalancePaymentError(null);

    try {
      let { amount, cash_amount, transfer_amount, payment_method, notes } = balancePaymentForm;

      amount = roundCurrency(Math.min(Math.max(amount, 0), balancePaymentDialog.remaining || 0));
      if (amount <= 0) {
        setBalancePaymentError('El monto debe ser mayor a 0.');
        setBalancePaymentLoading(false);
        return;
      }

      if (payment_method === 'cash') {
        cash_amount = amount;
        transfer_amount = 0;
      } else if (payment_method === 'transfer') {
        transfer_amount = amount;
        cash_amount = 0;
      } else {
        const combined = (cash_amount || 0) + (transfer_amount || 0);
        if (combined > 0 && Math.abs(combined - amount) > 0.01) {
          amount = roundCurrency(combined);
        }
      }

      const paymentData = {
        sale_id: balancePaymentDialog.saleId,
        installment_number: balancePaymentDialog.installmentNumber,
        amount,
        payment_method,
        cash_amount,
        transfer_amount,
        payment_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
        notes,
      };

      await api.post('/payments', paymentData);
      await fetchSales();

      const refreshed = await api.get(`/sales/${balancePaymentDialog.saleId}/installments`);
      const refreshedInstallments = (refreshed.data.installments || []).map(normalizeInstallmentDetail);

      setInstallmentsDialog((prev) => ({
        ...prev,
        installments: refreshedInstallments,
        success: 'Saldo cobrado correctamente.',
      }));

      closeBalancePaymentDialog();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      let message = 'No se pudo registrar el pago.';
      if (Array.isArray(detail)) {
        message = detail.map((item: any) => item?.msg || item?.detail || JSON.stringify(item)).join(' | ');
      } else if (detail && typeof detail === 'object') {
        message = detail.msg || detail.detail || detail.message || JSON.stringify(detail);
      } else if (typeof detail === 'string') {
        message = detail;
      }
      setBalancePaymentError(message);
    } finally {
      setBalancePaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Fade in timeout={500}>
        <Box>
          {/* Header */}
          <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={(theme) => ({ 
                fontWeight: 700, 
                mb: 1,
                ...(theme.palette.mode === 'dark'
                  ? { color: theme.palette.common.white }
                  : {
                      color: theme.palette.text.primary,
                      background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }),
              })}
            >
              Seguimiento de Ventas
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitorea el progreso de pagos de todas las ventas realizadas
            </Typography>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? 'fullWidth' : 'standard'}
            >
              <Tab value="tracking" label="Seguimiento de Ventas" />
              <Tab value="collections" label="Registro de cobranzas" />
            </Tabs>
          </Box>

          {activeTab === 'tracking' && (
            <>
          {/* Filters */}
          <Card sx={{ mb: { xs: 2, sm: 3 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Buscar por cliente, producto o teléfono..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as any)}
                      label="Estado"
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="active">Activas</MenuItem>
                      <MenuItem value="completed">Completadas</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Mora</InputLabel>
                    <Select
                      value={moraFilter}
                      onChange={(e) => setMoraFilter(e.target.value as 'all' | 'yes' | 'no')}
                      label="Mora"
                    >
                      <MenuItem value="all">Todas</MenuItem>
                      <MenuItem value="yes">Con mora</MenuItem>
                      <MenuItem value="no">Sin mora</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Vendedor</InputLabel>
                    <Select
                      value={vendorFilter}
                      onChange={(e) => setVendorFilter(e.target.value)}
                      label="Vendedor"
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="none">Sin vendedor</MenuItem>
                      {vendorOptions.map((v) => (
                        <MenuItem key={v} value={v}>{v}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Cobrador</InputLabel>
                    <Select
                      value={collectorFilter}
                      onChange={(e) => setCollectorFilter(e.target.value)}
                      label="Cobrador"
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="none">Sin cobrador</MenuItem>
                      {collectorOptions.map((c) => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Exportar XLSX">
                      <IconButton onClick={handleExportSalesXlsx} color="primary">
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Actualizar datos">
                      <IconButton onClick={fetchSales} color="primary">
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Sales Table */}
          <Card>
            <TableContainer>
              <Table size="small" sx={{ '& td, & th': { py: 0.75, px: 1 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={sortBy === 'date' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortBy === 'date'}
                        direction={sortBy === 'date' ? sortDirection : 'desc'}
                        onClick={() => handleSort('date')}
                      >
                        Fecha de venta
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'customer' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortBy === 'customer'}
                        direction={sortBy === 'customer' ? sortDirection : 'asc'}
                        onClick={() => handleSort('customer')}
                      >
                        Cliente
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'vendor' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortBy === 'vendor'}
                        direction={sortBy === 'vendor' ? sortDirection : 'asc'}
                        onClick={() => handleSort('vendor')}
                      >
                        Vendedor
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Cobrador</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Costo</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Valor de venta</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Ganancia</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Monto de la cuota</TableCell>
                    <TableCell align="center">Cuotas</TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Progreso</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Cobrado</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Por cobrar</TableCell>
                    <TableCell align="left" sx={{ display: { xs: 'table-cell', md: 'none' } }}>Resumen</TableCell>
                    <TableCell align="center">Mora</TableCell>
                    <TableCell align="center">Dia de cobro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSales.map((sale) => {
                    const arrearsDays = sale.arrears_days ?? 0;
                    const vendorBg = stringToColor((sale as any).vendor_name);
                    const vendorHover = hslToRgba(vendorBg, 0.08);
                    return (
                      <TableRow
                        key={sale.id}
                        hover
                        sx={{
                          ...getArrearsHighlight(arrearsDays),
                          position: 'relative',
                          borderLeft: `6px solid ${vendorBg}`,
                          height: 96,
                          '& > td': {
                            borderBottom: '3px solid rgba(0,0,0,0.18)',
                            height: 96,
                            py: 0,
                            verticalAlign: 'middle',
                          },
                          '&:hover': { backgroundColor: vendorHover },
                        }}
                      >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDate(sale.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {sale.customer_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600, letterSpacing: 0.3 }}
                          >
                            Venta #{sale.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(sale as any).vendor_name || '—'}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            letterSpacing: 0.2,
                            textTransform: 'uppercase',
                            backgroundColor: vendorBg,
                            color: 'white',
                            border: `1px solid ${vendorBg}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {(sale as any).collector_name ? (
                          <Chip
                            label={(sale as any).collector_name}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {(sale as any).notes && (sale as any).notes.startsWith('Productos: ')
                              ? (sale as any).notes.replace('Productos: ', '')
                              : sale.product_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">
                          {formatCurrency(sale.cost_price)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(sale.total_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                          {formatCurrency(getProfit(sale))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">
                          {formatCurrency(sale.installment_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => openInstallmentsDialog(sale)}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {sale.payments_count}/{sale.installments}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ minWidth: 120, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box>
                          <LinearProgress
                            variant="determinate"
                            value={getProgressPercentage(sale)}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                backgroundColor: sale.status === 'completed' ? 'success.main' : 'warning.main'
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontWeight: 500 }}>
                            {Math.round(getProgressPercentage(sale))}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCurrency(getCollectedAmount(sale))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">
                          {formatCurrency(getPendingAmount(sale))}
                        </Typography>
                      </TableCell>
                      {/* Resumen compacto para pantallas md y menores: incluye TODAS las métricas */}
                      <TableCell align="left" sx={{ display: { xs: 'table-cell', md: 'none' }, py: 1 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: '0.72rem' }}>
                          <Box>
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1.2 }}>Valor</Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{formatCurrency(sale.total_amount)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1.2 }}>Costo</Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{formatCurrency(sale.cost_price)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1.2 }}>Ganancia</Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#10B981' }}>
                              {formatCurrency(sale.total_amount - sale.cost_price)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1.2 }}>Cuota</Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{formatCurrency(sale.installment_amount)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1.2 }}>Cobrado</Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#10B981' }}>{formatCurrency(getCollectedAmount(sale))}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1.2 }}>Por cobrar</Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#EF4444' }}>{formatCurrency(getPendingAmount(sale))}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flex: 1, height: 4, backgroundColor: 'action.hover', borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${Math.min(100, getProgressPercentage(sale))}%`, backgroundColor: '#10B981' }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, minWidth: 32, textAlign: 'right' }}>
                            {Math.round(getProgressPercentage(sale))}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {sale.has_partial_installments ? (
                          <Chip
                            label={`Debe parte de cuota${sale.partial_outstanding_amount ? ` (${formatCurrency(sale.partial_outstanding_amount)})` : ''}`}
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        ) : arrearsDays ? (
                          <Chip
                            label={`${arrearsDays} ${arrearsDays === 1 ? 'día' : 'días'}`}
                            color={arrearsDays >= 28 ? 'error' : 'warning'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        ) : (
                          <Chip label="Sin mora" color="success" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={COLLECTION_DAY_LABELS[sale.collection_day || 0] || '—'}
                          size="small"
                          onClick={() => openCollectionDayDialog(sale)}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 600,
                            '&:hover': { opacity: 0.8 },
                          }}
                        />
                      </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredSales.length > 0 && (
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                        TOTAL
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                        {formatCurrency(totals.cost)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                        {formatCurrency(totals.total)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                        {formatCurrency(totals.profit)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                        -
                      </TableCell>
                      <TableCell align="center">-</TableCell>
                      <TableCell align="center" sx={{ minWidth: 120, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box>
                          <LinearProgress
                            variant="determinate"
                            value={totals.averageProgress}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                backgroundColor: 'primary.main',
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontWeight: 500 }}>
                            {Math.round(totals.averageProgress)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                        {formatCurrency(totals.collected)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                        {formatCurrency(totals.pending)}
                      </TableCell>
                      {/* Totales compactos en md y menores */}
                      <TableCell align="left" sx={{ display: { xs: 'table-cell', md: 'none' } }}>
                        <Typography variant="caption" sx={{ display: 'block' }}>Valor: <strong>{formatCurrency(totals.total)}</strong></Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>Cobrado: <strong>{formatCurrency(totals.collected)}</strong></Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>Por cobrar: <strong>{formatCurrency(totals.pending)}</strong></Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>Prog: <strong>{Math.round(totals.averageProgress)}%</strong></Typography>
                      </TableCell>
                      <TableCell align="center">-</TableCell>
                      <TableCell align="center">-</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredSales.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </Card>
            </>
          )}

          {activeTab === 'collections' && (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} md={4} lg={3}>
                  <Paper sx={{ p: 2 }}>
                    <DateCalendar value={selectedDate} onChange={handleDateChange} />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={8} lg={9}>
                  <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 600 }}>
                          {selectedDate ? `Cobranzas del ${formatDateFns(selectedDate, 'dd/MM/yyyy')}` : 'Cobranzas'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Registros de cobros y reprogramaciones del día seleccionado
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1} alignItems="center">
                        <Tooltip title="Actualizar datos">
                          <span>
                            <IconButton
                              onClick={() => selectedDate && fetchCollections(selectedDate)}
                              color="primary"
                              disabled={collectionsLoading || !selectedDate}
                            >
                              <RefreshIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>

                    {collectionsError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {collectionsError}
                      </Alert>
                    )}

                    <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          onClick={() => handleCollectionFilterChange('all')}
                          onKeyDown={handleCollectionFilterKeyDown('all')}
                          role="button"
                          tabIndex={0}
                          sx={(theme) => ({
                            background:
                              theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(99, 102, 241, 0.35) 100%)'
                                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                            cursor: 'pointer',
                            border: collectionsStatusFilter === 'all'
                              ? `2px solid ${theme.palette.primary.main}`
                              : '2px solid transparent',
                            boxShadow: collectionsStatusFilter === 'all'
                              ? (theme.palette.mode === 'dark'
                                  ? '0 12px 32px rgba(99, 102, 241, 0.45)'
                                  : '0 10px 30px rgba(99, 102, 241, 0.15)')
                              : 'none',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme.palette.mode === 'dark'
                                ? '0 12px 28px rgba(99, 102, 241, 0.35)'
                                : '0 8px 24px rgba(99, 102, 241, 0.12)',
                            },
                            '&:focus-visible': {
                              outline: `2px solid ${theme.palette.primary.main}`,
                              outlineOffset: '2px',
                            },
                          })}
                        >
                          <CardContent>
                            <Typography
                              color="textSecondary"
                              gutterBottom
                              sx={(theme) => ({ fontWeight: 500, color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.text.secondary })}
                            >
                              Total
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={(theme) => ({ fontWeight: 700, color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary })}
                            >
                              {collectionsSummary.total}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          onClick={() => handleCollectionFilterChange('pending')}
                          onKeyDown={handleCollectionFilterKeyDown('pending')}
                          role="button"
                          tabIndex={0}
                          sx={(theme) => ({
                            background:
                              theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.25) 0%, rgba(217, 119, 6, 0.4) 100%)'
                                : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                            cursor: 'pointer',
                            border: collectionsStatusFilter === 'pending'
                              ? `2px solid ${theme.palette.warning.main}`
                              : '2px solid transparent',
                            boxShadow: collectionsStatusFilter === 'pending'
                              ? (theme.palette.mode === 'dark'
                                  ? '0 12px 32px rgba(245, 158, 11, 0.45)'
                                  : '0 10px 30px rgba(245, 158, 11, 0.2)')
                              : 'none',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme.palette.mode === 'dark'
                                ? '0 12px 28px rgba(245, 158, 11, 0.35)'
                                : '0 8px 24px rgba(245, 158, 11, 0.18)',
                            },
                            '&:focus-visible': {
                              outline: `2px solid ${theme.palette.warning.main}`,
                              outlineOffset: '2px',
                            },
                          })}
                        >
                          <CardContent>
                            <Typography
                              color="textSecondary"
                              gutterBottom
                              sx={(theme) => ({ fontWeight: 500, color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.text.secondary })}
                            >
                              Pendientes
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={(theme) => ({ fontWeight: 700, color: theme.palette.mode === 'dark' ? '#fbbf24' : theme.palette.text.primary })}
                            >
                              {collectionsSummary.pending}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          onClick={() => handleCollectionFilterChange('rescheduled')}
                          onKeyDown={handleCollectionFilterKeyDown('rescheduled')}
                          role="button"
                          tabIndex={0}
                          sx={(theme) => ({
                            background:
                              theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.25) 0%, rgba(124, 58, 237, 0.4) 100%)'
                                : 'linear-gradient(135deg, #ede9fe 0%, #c4b5fd 100%)',
                            cursor: 'pointer',
                            border: collectionsStatusFilter === 'rescheduled'
                              ? `2px solid ${theme.palette.secondary.main}`
                              : '2px solid transparent',
                            boxShadow: collectionsStatusFilter === 'rescheduled'
                              ? (theme.palette.mode === 'dark'
                                  ? '0 12px 32px rgba(139, 92, 246, 0.45)'
                                  : '0 10px 30px rgba(139, 92, 246, 0.2)')
                              : 'none',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme.palette.mode === 'dark'
                                ? '0 12px 28px rgba(139, 92, 246, 0.35)'
                                : '0 8px 24px rgba(139, 92, 246, 0.18)',
                            },
                            '&:focus-visible': {
                              outline: `2px solid ${theme.palette.secondary.main}`,
                              outlineOffset: '2px',
                            },
                          })}
                        >
                          <CardContent>
                            <Typography
                              color="textSecondary"
                              gutterBottom
                              sx={(theme) => ({ fontWeight: 500, color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.text.secondary })}
                            >
                              Reprogramadas
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={(theme) => ({ fontWeight: 700, color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.text.primary })}
                            >
                              {collectionsSummary.rescheduled}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          onClick={() => handleCollectionFilterChange('paid')}
                          onKeyDown={handleCollectionFilterKeyDown('paid')}
                          role="button"
                          tabIndex={0}
                          sx={(theme) => ({
                            background:
                              theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.25) 0%, rgba(22, 163, 74, 0.4) 100%)'
                                : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                            cursor: 'pointer',
                            border: collectionsStatusFilter === 'paid'
                              ? `2px solid ${theme.palette.success.main}`
                              : '2px solid transparent',
                            boxShadow: collectionsStatusFilter === 'paid'
                              ? (theme.palette.mode === 'dark'
                                  ? '0 12px 32px rgba(34, 197, 94, 0.45)'
                                  : '0 10px 30px rgba(34, 197, 94, 0.2)')
                              : 'none',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme.palette.mode === 'dark'
                                ? '0 12px 28px rgba(34, 197, 94, 0.35)'
                                : '0 8px 24px rgba(34, 197, 94, 0.18)',
                            },
                            '&:focus-visible': {
                              outline: `2px solid ${theme.palette.success.main}`,
                              outlineOffset: '2px',
                            },
                          })}
                        >
                          <CardContent>
                            <Typography
                              color="textSecondary"
                              gutterBottom
                              sx={(theme) => ({ fontWeight: 500, color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.text.secondary })}
                            >
                              Cobradas
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={(theme) => ({ fontWeight: 700, color: theme.palette.mode === 'dark' ? '#4ade80' : theme.palette.text.primary })}
                            >
                              {collectionsSummary.paid}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {collectionsLoading ? (
                      <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </Box>
                    ) : filteredDailyCollections.length === 0 ? (
                      <Box py={4} textAlign="center">
                        <Typography variant="body2" color="text.secondary">
                          {collectionsStatusFilter === 'all'
                            ? 'No hay cobranzas registradas para esta fecha.'
                            : 'No hay cobranzas que coincidan con el filtro seleccionado.'}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Cliente</TableCell>
                                <TableCell>Dirección</TableCell>
                                <TableCell>Producto</TableCell>
                                <TableCell>Cuota</TableCell>
                                <TableCell align="right">Monto</TableCell>
                                <TableCell>Nota / Motivo</TableCell>
                                <TableCell align="center">Estado</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredDailyCollections.map((collection) => {
                                const customerFullName = `${collection.customer_surname} ${collection.customer_name}`.trim();
                                const status = getCollectionStatus(collection);
                                return (
                                  <TableRow
                                    key={`${collection.sale_id}-${collection.installment_number}-${collection.due_date}`}
                                    hover
                                  >
                                    <TableCell sx={{ minWidth: 160 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {customerFullName}
                                      </Typography>
                                      {collection.customer_phone && (
                                        <Typography variant="caption" color="text.secondary">
                                          {collection.customer_phone}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 160 }}>
                                      <Typography variant="body2">{collection.customer_address}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 160 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {collection.notes && collection.notes.startsWith('Productos: ')
                                          ? collection.notes.replace('Productos: ', '')
                                          : collection.product_name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {`${collection.installment_number}/${collection.total_installments}`}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(collection.installment_amount)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>
                                      {collection.last_payment_note ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                          Nota del pago: {collection.last_payment_note}
                                        </Typography>
                                      ) : null}
                                      {collection.last_reschedule_reason ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', mt: collection.last_payment_note ? 0.5 : 0 }}
                                        >
                                          Motivo reprogramación: {collection.last_reschedule_reason}
                                        </Typography>
                                      ) : null}
                                      {!collection.last_payment_note && !collection.last_reschedule_reason && (
                                        <Typography variant="caption" color="text.secondary">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip label={status.label} color={status.color} size="small" />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleExportCollectionsXlsx}>
                            Exportar XLSX
                          </Button>
                        </Box>
                      </>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{ mt: 5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Historial de operaciones por venta
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Cliente</InputLabel>
                      <Select
                        value={selectedCustomerId === '' ? '' : String(selectedCustomerId)}
                        label="Cliente"
                        onChange={handleCustomerSelect}
                      >
                        <MenuItem value="">Selecciona un cliente</MenuItem>
                        {customersOptions.map((customer) => (
                          <MenuItem key={customer.id} value={customer.id.toString()}>
                            {customer.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={selectedCustomerId === '' || customerSales.length === 0}
                    >
                      <InputLabel>Venta</InputLabel>
                      <Select
                        value={selectedSaleId === '' ? '' : String(selectedSaleId)}
                        label="Venta"
                        onChange={handleSaleSelect}
                      >
                        <MenuItem value="">Selecciona una venta</MenuItem>
                        {customerSales.map((sale) => (
                          <MenuItem key={sale.id} value={sale.id.toString()}>
                            {`${sale.product_name} - ${formatDate(sale.created_at)}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {saleOperationsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {saleOperationsError}
                  </Alert>
                )}

                {saleOperationsLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : selectedSaleId === '' ? (
                  <Alert severity="info">Selecciona un cliente y una venta para ver el detalle.</Alert>
                ) : saleOperations.length === 0 ? (
                  <Alert severity="info">No hay operaciones registradas para esta venta.</Alert>
                ) : (
                  <>
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Cuota</TableCell>
                            <TableCell align="center">Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {saleOperations.map((detail) => {
                            const status = getInstallmentStatus(detail);
                            return (
                              <TableRow key={`operation-${detail.installment_number}`}>
                                <TableCell>{formatDate(detail.due_date)}</TableCell>
                                <TableCell>{`${detail.installment_number}/${detail.total_installments}`}</TableCell>
                                <TableCell align="center">
                                  <Chip label={status.label} color={status.color} size="small" />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleExportOperationsXlsx}>
                        Exportar XLSX
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </LocalizationProvider>
          )}
        </Box>
      </Fade>
      <Dialog
        open={installmentsDialog.open}
        onClose={closeInstallmentsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {`Detalle de cuotas - ${installmentsDialog.sale?.customer_name ?? ''}`}
        </DialogTitle>
        <DialogContent dividers>
          {installmentsDialog.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {installmentsDialog.error && (
            <Alert severity="error">{installmentsDialog.error}</Alert>
          )}

          {installmentsDialog.success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {installmentsDialog.success}
            </Alert>
          )}

          {!installmentsDialog.loading && !installmentsDialog.error && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nº Cuota</TableCell>
                  <TableCell align="right">Valor Cuota</TableCell>
                  <TableCell>Fecha Cuota</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {installmentsDialog.installments.map((detail) => (
                  <TableRow key={detail.installment_number}>
                    <TableCell>{`${detail.installment_number}/${detail.total_installments}`}</TableCell>
                    <TableCell align="right" sx={{ minWidth: 160 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          type="number"
                          value={detail.current_amount}
                          size="small"
                          onChange={(e) => handleInstallmentAmountChange(
                            detail.installment_number,
                            parseFloat(e.target.value)
                          )}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          disabled={savingInstallment === detail.installment_number || !installmentsDialog.sale}
                          onClick={() => handleSaveInstallmentAmount(detail.installment_number, detail.current_amount)}
                        >
                          {savingInstallment === detail.installment_number ? 'Guardando...' : 'Guardar'}
                        </Button>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Original: {formatCurrency(detail.original_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: detail.is_rescheduled ? 600 : 400 }}
                          >
                            {formatDate(detail.due_date)}
                          </Typography>
                          {detail.is_rescheduled && (
                            <Chip
                              label={`Reprogramada${detail.reschedule_count && detail.reschedule_count > 1 ? ` x${detail.reschedule_count}` : ''}`}
                              color="info"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Box>
                        {detail.is_rescheduled && (
                          <>
                            {detail.previous_due_date && detail.previous_due_date !== detail.original_due_date && (
                              <Typography variant="caption" color="text.secondary">
                                Última fecha previa: {formatDate(detail.previous_due_date)}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              Fecha original: {formatDate(detail.original_due_date)}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isInstallmentPartial(detail) ? (
                        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                          Debe {formatCurrency(computeInstallmentRemaining(detail))}
                        </Typography>
                      ) : (
                        <Chip
                          label={detail.is_paid ? 'Cobrado' : 'Pendiente'}
                          color={detail.is_paid ? 'success' : 'warning'}
                          size="small"
                        />
                      )}
                      {detail.last_payment_note && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.75 }}
                        >
                          Nota del pago: {detail.last_payment_note}
                        </Typography>
                      )}
                      {detail.last_reschedule_reason && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: detail.last_payment_note ? 0.5 : 0.75 }}
                        >
                          Motivo reprogramación: {detail.last_reschedule_reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {isInstallmentPartial(detail) ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => openBalancePaymentDialogFor(detail)}
                          disabled={balancePaymentLoading}
                        >
                          Cobrar saldo
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeInstallmentsDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={balancePaymentDialog.open}
        onClose={() => {
          if (!balancePaymentLoading) {
            closeBalancePaymentDialog();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {`Cobrar saldo - ${balancePaymentDialog.customerName || ''}`}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2">
                <strong>Saldo pendiente:</strong> {formatCurrency(balancePaymentDialog.remaining)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ingresa el monto a cobrar (máximo {formatCurrency(balancePaymentDialog.remaining)}).
              </Typography>
            </Box>

            {balancePaymentError && (
              <Alert severity="error">{balancePaymentError}</Alert>
            )}

            <TextField
              label="Monto a cobrar"
              type="number"
              fullWidth
              value={balancePaymentForm.amount}
              onChange={(e) => handleBalanceAmountChange(parseFloat(e.target.value))}
              inputProps={{ min: 0, max: balancePaymentDialog.remaining, step: 0.01 }}
              disabled={balancePaymentLoading}
            />

            <FormControl fullWidth>
              <InputLabel>Método de pago</InputLabel>
              <Select
                value={balancePaymentForm.payment_method}
                onChange={(e) => handleBalanceMethodChange(e.target.value as 'cash' | 'transfer' | 'mixed')}
                label="Método de pago"
                disabled={balancePaymentLoading}
              >
                <MenuItem value="cash">Efectivo</MenuItem>
                <MenuItem value="transfer">Transferencia</MenuItem>
                <MenuItem value="mixed">Mixto</MenuItem>
              </Select>
            </FormControl>

            {balancePaymentForm.payment_method === 'mixed' && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Monto en efectivo"
                    type="number"
                    fullWidth
                    value={balancePaymentForm.cash_amount}
                    onChange={(e) => handleBalanceCashChange(parseFloat(e.target.value))}
                    inputProps={{ min: 0, step: 0.01, max: balancePaymentForm.amount }}
                    disabled={balancePaymentLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Monto por transferencia"
                    type="number"
                    fullWidth
                    value={balancePaymentForm.transfer_amount}
                    onChange={(e) => handleBalanceTransferChange(parseFloat(e.target.value))}
                    inputProps={{ min: 0, step: 0.01, max: balancePaymentForm.amount }}
                    disabled={balancePaymentLoading}
                  />
                </Grid>
              </Grid>
            )}

            <TextField
              label="Notas (opcional)"
              multiline
              rows={2}
              fullWidth
              value={balancePaymentForm.notes}
              onChange={(e) => setBalancePaymentForm((prev) => ({ ...prev, notes: e.target.value }))}
              disabled={balancePaymentLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBalancePaymentDialog} disabled={balancePaymentLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleBalancePaymentSubmit}
            variant="contained"
            disabled={balancePaymentLoading || balancePaymentForm.amount <= 0}
          >
            {balancePaymentLoading ? 'Registrando...' : 'Registrar pago'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para editar dia de cobro */}
      <Dialog
        open={collectionDayDialog.open}
        onClose={() => setCollectionDayDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cambiar dia de cobro</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {collectionDayDialog.customerName} — Venta #{collectionDayDialog.saleId}
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Dia de cobro</InputLabel>
            <Select
              value={collectionDayDialog.newDay}
              label="Dia de cobro"
              onChange={(e) =>
                setCollectionDayDialog((prev) => ({
                  ...prev,
                  newDay: Number(e.target.value),
                }))
              }
            >
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <MenuItem key={day} value={day}>
                  {COLLECTION_DAY_LABELS[day]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCollectionDayDialog((prev) => ({ ...prev, open: false }))}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveCollectionDay}
            variant="contained"
            disabled={savingCollectionDay}
          >
            {savingCollectionDay ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
